import React from 'react';

import * as ROUTES from '@constants/routes';

import {
  useAdminAddPolicyToRole,
  useAdminListPolicies,
  useAdminRemovePolicyFromRole,
} from 'generated/admin.generated';
import type { RoleModel } from 'generated/types';

import { Editor } from '../Common';

const messages: Editor.EditorMessages = {
  add: { id: 'admin.roles.policies.add' },
  remove: { id: 'admin.roles.policies.remove' },
  confirmAdd: {
    id: 'admin.roles.policies.add.confirm',
  },
  confirmRemove: {
    id: 'admin.roles.policies.remove.confirm',
  },
  title: { id: 'admin.roles.associated.policies' },
};

interface PolicyEditorProps {
  role: RoleModel | undefined;
  loading: boolean;
}

const PolicyPolicyEditor = ({
  role,
  loading: baseLoading,
}: PolicyEditorProps) => {
  const { data: policiesData, loading: policiesLoading } =
    useAdminListPolicies();
  const [addPolicy] = useAdminAddPolicyToRole();
  const [removePolicy] = useAdminRemovePolicyFromRole();

  const handleAddPolicy = async (option: Editor.EditorOption) => {
    if (!role) {
      return;
    }
    await addPolicy({
      variables: {
        input: {
          policy_id: option.id,
          role_id: role.role_id,
        },
      },
    });
  };

  const handleRemovePolicy = async (option?: Editor.EditorOption) => {
    if (!option || !role) {
      return;
    }
    await removePolicy({
      variables: {
        input: {
          policy_id: option.id,
          role_id: role.role_id,
        },
      },
    });
  };

  const loading = baseLoading || policiesLoading;

  const options = React.useMemo(() => {
    return policiesData?.listPolicies.map<Editor.EditorOption>((policy) => ({
      id: policy.policy_id,
      name: policy.policy_name ?? '',
      url: `${ROUTES.ADMIN_MANAGE_POLICIES}${policy.policy_id}`,
    }));
  }, [policiesData]);

  const selected = React.useMemo(() => {
    return role?.policies?.map<Editor.EditorOption>((policy) => ({
      id: policy.policy_id,
      name: policy.policy_name ?? '',
      url: `${ROUTES.ADMIN_MANAGE_POLICIES}${policy.policy_id}`,
    }));
  }, [role]);

  return (
    <Editor.default
      selected={selected}
      options={options}
      messages={messages}
      onRemove={handleRemovePolicy}
      onAdd={handleAddPolicy}
      loading={loading}
    />
  );
};

export default PolicyPolicyEditor;
